import {useCallback, useEffect, useMemo, useState} from 'react';

import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  buildCompanyMatchesFilterParamsRecord,
  getActiveMatchesPageStatFilter,
} from '../features/company/recruiting/matches/companyMatchesListFilters';
import {MatchFilterOptions} from '../features/company/recruiting/matches/MatchFilters/MatchFilters';
import {GetRoutes} from '../lib/api/GET/GetRoutes';
import {AppConfig} from '../lib/AppConfig';
import {MatchData} from '../types/shared-recruiting-types';
import {Option} from '../types/shared-types';

import {MATCHES_SUMMARY_QUERY_KEY, useMatchesSummary} from './useMatchesSummary';
import {useQueryPagination} from './useQueryPagination';
import {LocalQueryParams, useQueryParameterState} from './useQueryParameterState';

interface Filters extends LocalQueryParams {
  job?: string;
  status?: string;
}

type MatchStatusMenuKey = 'accepted' | 'contacted' | 'onHold' | 'interview' | 'hired' | 'rejected';

const matchStatusIdToMenuKey = (id: string): MatchStatusMenuKey | null => {
  if (id === 'on_hold') return 'onHold';
  if (id === 'accepted' || id === 'contacted' || id === 'interview' || id === 'hired' || id === 'rejected') {
    return id;
  }
  return null;
};

export const useMatchesSorting = (companyId: string | undefined) => {
  const queryClient = useQueryClient();
  const [matches, setMatches] = useState<MatchData[] | null>(null);
  const [initialJobs, setInitialJobs] = useState<Option[]>([]);
  const [initialStatuses, setInitialStatuses] = useState<Option[]>([]);
  const {t} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.menuItems'});
  const {updateQueryParameters, params} = useQueryParameterState<Filters>();

  const matchesApiFilters = useMemo(
    () => buildCompanyMatchesFilterParamsRecord(params.job, params.status),
    [params.job, params.status]
  );

  const activeStatFilter = useMemo(
    () => getActiveMatchesPageStatFilter(params.job, params.status),
    [params.job, params.status]
  );

  const {
    data: summaryPayload,
    isPending: matchesSummaryLoading,
    error: matchesSummaryError,
  } = useMatchesSummary(companyId, params.job, params.status);

  const {
    data,
    pagination,
    hasNext,
    getNext,
    isLoading: isMatchesLoading,
    error: matchesListError,
    loadingInitialData,
    refreshData: refreshMatchesList,
  } = useQueryPagination<MatchData, Record<string, number>>({
    url: GetRoutes.companyMatches(companyId ?? ''),
    entity: 'matches',
    perPage: AppConfig.PAGINATION_PER_PAGE,
    reverseOrder: true,
    enabled: Boolean(companyId),
    filterParams: matchesApiFilters,
  });

  const refreshData = useCallback(() => {
    refreshMatchesList();
    if (companyId) {
      queryClient.resetQueries({queryKey: [MATCHES_SUMMARY_QUERY_KEY, companyId]});
    }
  }, [refreshMatchesList, queryClient, companyId]);

  const matchesSummaryCounts = summaryPayload?.companyMatchesSummary;
  const error = matchesListError || matchesSummaryError;

  useEffect(() => {
    if (!data) return;
    const job = params.job?.split(',').filter(Boolean) || [];
    const status = params.status?.split(',').filter(Boolean) || [];

    setInitialJobs(
      job.map((resourceId) => {
        const title = data.find((entry) => entry.companyJobPosting?.resourceId === resourceId)?.companyJobPosting
          ?.title;
        return {id: resourceId, label: title?.trim() ? title : resourceId};
      })
    );
    setInitialStatuses(
      status.map((id) => {
        const menuKey = matchStatusIdToMenuKey(id);
        return {
          id,
          label: menuKey ? String(t(menuKey)) : id.replace(/_/g, ' '),
        };
      })
    );

    setMatches([...data]);
  }, [data, params.job, params.status, t]);

  const filterBy = useCallback(
    (filters: MatchFilterOptions) => {
      const {job, status} = filters;

      if (!job.length && !status.length) {
        updateQueryParameters({job: '', status: ''});
        return;
      }

      updateQueryParameters({
        status: status?.map((option) => option.id).join(','),
        job: job?.map((option) => option.id).join(','),
      });
    },
    [updateQueryParameters]
  );

  const jobOptions: Option[] = useMemo(() => {
    if (!data) return [];

    const opts: Option[] = [];
    const seen = new Set<string>();
    for (const entry of data) {
      const resourceId = entry.companyJobPosting?.resourceId?.trim();
      const title = entry.companyJobPosting?.title?.trim();
      if (!resourceId || seen.has(resourceId)) continue;
      seen.add(resourceId);
      opts.push({id: resourceId, label: title || resourceId});
    }
    return opts;
  }, [data]);

  const statusOptions: Option[] = useMemo(() => {
    return [
      {
        id: 'accepted',
        label: t('accepted'),
      },
      {
        id: 'contacted',
        label: t('contacted'),
      },
      {
        id: 'on_hold',
        label: t('onHold'),
      },
      {
        id: 'interview',
        label: t('interview'),
      },
      {
        id: 'hired',
        label: t('hired'),
      },
      {
        id: 'rejected',
        label: t('rejected'),
      },
    ];
  }, [t]);

  return {
    matches,
    loadingInitialData,
    error,
    matchesListError,
    matchesSummaryError,
    matchesSummaryCounts,
    matchesSummaryLoading,
    pagination,
    hasNext,
    getNext,
    isMatchesLoading,
    refreshData,
    filterBy,
    jobOptions,
    statusOptions,
    initialJobs,
    initialStatuses,
    activeStatFilter,
  };
};
